import { useState, useRef, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { ClerkProvider, SignIn, useAuth } from '@clerk/clerk-react';
import { Toaster } from 'react-hot-toast';
import { usePostHog } from 'posthog-js/react';
import Sidebar from './components/Sidebar';
import Home from './components/Home';
import Sheet from './components/Sheet';
import Templates from './components/Templates';
import Pricing from './components/Pricing';
import Import from './components/Import';
import Redeem from './components/Redeem';
import { useUserStorage } from './services/userDataStorage';
import './App.css';

// Import your publishable key
const isDevelopment = process.env.NODE_ENV === 'development';
const PUBLISHABLE_KEY = isDevelopment 
  ? process.env.REACT_APP_CLERK_PUBLISHABLE_KEY_DEV 
  : process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  const expectedKey = isDevelopment 
    ? "REACT_APP_CLERK_PUBLISHABLE_KEY_DEV" 
    : "REACT_APP_CLERK_PUBLISHABLE_KEY";
  throw new Error(`Missing Publishable Key. Please add ${expectedKey} to your .env.local file`)
}

function App() {
  return (
    <ClerkProvider 
      publishableKey={PUBLISHABLE_KEY!}
      signUpFallbackRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      domain={undefined}
      proxyUrl={undefined}
    >
      <AppContent />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#10b981',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#ef4444',
            },
          },
        }}
      />
    </ClerkProvider>
  );
}

// Separate component to use auth hooks inside ClerkProvider
function AppContent() {
  const { isSignedIn, isLoaded, userId } = useAuth();
  const posthog = usePostHog();

  // Track authentication events
  useEffect(() => {
    if (isLoaded && isSignedIn && userId) {
      posthog?.identify(userId);
      posthog?.capture('user_signed_in');
    }
  }, [isSignedIn, isLoaded, userId, posthog]);

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="auth-container auth-container-loading">
        <div className="loading-screen">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="App">
        <div className="auth-container auth-container-signin">
          <div className="auth-card-signin">
            <SignIn 
              routing="hash"
              signUpUrl="#/sign-up"
              afterSignInUrl="/"
              appearance={{
                elements: {
                  rootBox: "mx-auto",
                  card: "shadow-none border-0"
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <AuthenticatedApp />
    </div>
  );
}

// Component to handle initial redirect logic
function InitialRedirect() {
  const navigate = useNavigate();
  const userStorage = useUserStorage();
  const [isChecking, setIsChecking] = useState(true);
  const hasRedirectedRef = useRef(false);
  const posthog = usePostHog();

  useEffect(() => {
    // Prevent multiple redirects
    if (hasRedirectedRef.current) {
      return;
    }

    const checkAndRedirect = async () => {
      try {
        // Wait for Firebase to be ready
        if (!userStorage?.isFirebaseReady) {
          return;
        }

        const grids = await userStorage.getAllGrids();
        
        if (grids.length === 0) {
          // No grids exist, create one and redirect to it
          const newGrid = await userStorage.createNewGrid();
          hasRedirectedRef.current = true;
          posthog?.capture('first_grid_created');
          navigate(`/sheets/${newGrid.id}`, { replace: true });
        } else {
          // User has grids, redirect to home
          hasRedirectedRef.current = true;
          posthog?.capture('app_opened_existing_user', { grid_count: grids.length });
          navigate('/home', { replace: true });
        }
        
        setIsChecking(false);
      } catch (error) {
        console.error('Error during initial redirect:', error);
        setIsChecking(false);
      }
    };

    checkAndRedirect();
  }, [navigate, userStorage, posthog]);

  if (isChecking) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  return null;
}

// Authenticated app component
function AuthenticatedApp() {
  const userStorage = useUserStorage();
  const [sidebarRefreshCounter, setSidebarRefreshCounter] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [subscriptionRefreshFn, setSubscriptionRefreshFn] = useState<(() => void) | null>(null);
  const posthog = usePostHog();
  
  // Add debugging when function is set
  const handleSetSubscriptionRefresh = useCallback((refreshFn: () => void) => {
    setSubscriptionRefreshFn(() => refreshFn);
  }, []);
  
  const navigate = useNavigate();

  const triggerSidebarRefresh = () => {
    setSidebarRefreshCounter(prev => prev + 1);
  };

  const triggerSubscriptionRefresh = async () => {
    if (subscriptionRefreshFn && typeof subscriptionRefreshFn === 'function') {
      try {
        await subscriptionRefreshFn();
      } catch (error) {
        console.error('Error calling subscription refresh function:', error);
      }
    } else {
      console.warn('Subscription refresh function not available or not a function:', typeof subscriptionRefreshFn);
    }
  };

  const toggleSidebar = () => {
    const newVisibility = !sidebarVisible;
    setSidebarVisible(newVisibility);
    posthog?.capture('sidebar_toggled', { visible: newVisibility });
  };
  
  const handleDeleteGrid = async (id: string) => {
    try {
      if (!userStorage) {
        console.error('App: userStorage is not available');
        return;
      }
      
      await userStorage.deleteGrid(id);
      posthog?.capture('grid_deleted', { grid_id: id });
      triggerSidebarRefresh();
    } catch (error) {
      console.error('Error deleting grid:', error);
      posthog?.capture('grid_delete_failed', { grid_id: id, error: String(error) });
      // Still trigger refresh to update the sidebar in case of partial success
      triggerSidebarRefresh();
      // Re-throw the error so calling code knows deletion failed
      throw error;
    }
  };

  // Handle grid selection from sidebar for routing
  const handleGridSelect = (gridId: string) => {
    posthog?.capture('grid_selected', { grid_id: gridId, source: 'sidebar' });
    // Navigate to the sheet page using React Router
    navigate(`/sheets/${gridId}`);
  };

  return (
    <div className="app-container">
      {sidebarVisible && (
        <Sidebar 
          onHideSidebar={toggleSidebar}
          onGridSelect={handleGridSelect}
          activeGridId={undefined} // Not needed for routing version
          onDeleteGrid={handleDeleteGrid}
          onSidebarRefresh={triggerSidebarRefresh}
          refreshCounter={sidebarRefreshCounter}
          onSetSubscriptionRefresh={handleSetSubscriptionRefresh}
        />
      )}
      <div className="main-content-wrapper">
        <Routes>
          <Route path="/" element={<InitialRedirect />} />
          <Route 
            path="/home" 
            element={<Home onSidebarRefresh={triggerSidebarRefresh} />} 
          />
          <Route 
            path="/templates" 
            element={<Templates onSidebarRefresh={triggerSidebarRefresh} />} 
          />
          <Route 
            path="/sheets/:id" 
            element={
              <Sheet 
                onSidebarRefresh={triggerSidebarRefresh} 
                sidebarVisible={sidebarVisible} 
                onShowSidebar={toggleSidebar}
                onSubscriptionRefresh={triggerSubscriptionRefresh}
              />
            } 
          />
          <Route 
            path="/pricing" 
            element={<Pricing />} 
          />
          <Route 
            path="/import" 
            element={<Import onSidebarRefresh={triggerSidebarRefresh} />} 
          />
          <Route 
            path="/redeem" 
            element={<Redeem />} 
          />
        </Routes>
      </div>
    </div>
  );
}

export default App; 